import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';

import { DprProgressWithoutTargetGridComponent } from '@dpr-details/components';
import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { DprProgressWithoutTargetGridData } from '@dpr-details/models/grid';
import { DprProgressWithoutTargetGridSync } from '@dpr-details/models/sync';
import { DprDetailsService } from '@dpr-details/services';
import { DprProgressWithoutTargetGridService } from '@dpr-details/services/grid';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { GridSync } from '@grid/models';
import { StaticMessage } from '@shared/enums';
import { DprDetailsSync, DprProgressWithoutTarget } from '@shared/models/dpr';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { ScreenSizeService, ToastNotificationService } from '@shared/services';
import { getStaticMessage } from '@shared/utils';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';

@UntilDestroy()
@Component({
  selector: 'app-dpr-progress-without-target-page',
  templateUrl: './dpr-progress-without-target-page.component.html',
  styleUrls: ['./dpr-progress-without-target-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprProgressWithoutTargetPageComponent
  extends DprDetailsPageBaseComponent<DprProgressWithoutTargetGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprProgress.txtProgressWithoutTargetRegistrations:Progress Without Target`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprProgressWithoutTargetGridService: DprProgressWithoutTargetGridService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private toastNotificationService: ToastNotificationService,
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  private get dprProgressWithoutTarget(): DprProgressWithoutTarget {
    return this.dprDetails.dprProgressWithoutTarget;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    if (
      this.dprProgressWithoutTargetGridService.shouldAutoFill(
        this.registrationsGridData
      )
    ) {
      this.autoFillEmptyItems();
    }

    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(this.attachmentsChanges);

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => (this.isSaving = false)),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  //#region Protected methods

  protected setupData(): void {
    this.registrationsGridData =
      this.dprProgressWithoutTargetGridService.buildGridData(
        this.dprDetails.dprProgressWithoutTarget
          .dprProgressWithoutTargetDetails,
        this.selectedSubproject.data?.progressWithoutTarget
          .progressWithoutTargetItems
      );

    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprProgressWithoutTarget?.dprProgressWithoutTargetAttachments
    );

    this.comment = this.dprProgressWithoutTarget.comment;
  }

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprProgressWithoutTargetGridComponent;
  }

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprProgressWithoutTargetGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprProgressWithoutTarget
        ?.syncDprProgressWithoutTargetDetails;

    if (!offlineChanges) {
      return null;
    }

    const offlineGridData =
      this.dprProgressWithoutTargetGridService.buildGridData(
        [
          ...(offlineChanges.addedList ?? []),
          ...(offlineChanges.updatedList ?? []),
        ],
        this.selectedSubproject.data.progressWithoutTarget
          .progressWithoutTargetItems
      );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): Sync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprProgressWithoutTarget
        ?.syncDprProgressWithoutTargetAttachments;

    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#region Private methods

  private validateChanges(
    attachmentsChanges: Sync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationChanges: Sync<DprProgressWithoutTargetGridData>,
    attachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgressWithoutTarget> {
    const syncDprProgressWithoutTargetDetails =
      this.dprProgressWithoutTargetGridService.mapToGridSync(
        registrationChanges,
        this.dprProgressWithoutTarget.id
      );

    const syncData: DprProgressWithoutTargetGridSync =
      this.dprDetailsSyncModelBuilderService.forDprProgressWithoutTarget(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprProgressWithoutTarget.id,
        syncDprProgressWithoutTargetDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprProgressWithoutTarget(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.progressWithoutTargetTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprProgressWithoutTarget): void {
    this.refreshData();
    this.offlineFacade.setDprProgressWithoutTarget(result, this.dprDetails.id);
  }

  private autoFillEmptyItems(): void {
    if (!this.registrationsGridData?.length) {
      return;
    }

    this.toastNotificationService.info(
      getStaticMessage(
        StaticMessage.AutoFillText,
        this.customTabNames.progressWithoutTargetTabName
      ),
      false,
      getStaticMessage(
        StaticMessage.AutoFillTitle,
        this.customTabNames.progressWithoutTargetTabName
      )
    );

    this.registrationsGridData.forEach((x) => {
      if (x.clearedToday == null) {
        this.registrationsGridApi.editCell(x, 'clearedToday', 0);
      }
    });

    this.registrationsChanges = this.registrationsGridApi.getChanges();
  }
}
